import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  addTitleAccountUserMutation,
  cancelTitleAccountInviteMutation,
  disableTitleAccountUserMutation,
  getTitleAccountUsersOptions,
  makeTitleAccountPrimaryMutation,
  reenableTitleAccountUserMutation,
  updateTitleAccountUserAdminMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

type TitleUser = {
  id: string;
  email: string;
  name?: string | null;
  is_admin: boolean;
  primary_id?: string | null;
  status: string;
  is_disabled: boolean;
};

export function TitleAccountUsers({
  currentAccountId,
  isPrimary,
}: {
  currentAccountId: string;
  isPrimary: boolean;
}) {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const queryClient = useQueryClient();
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [inviteError, setInviteError] = useState<string | null>(null);

  const usersQuery = useQuery({
    ...getTitleAccountUsersOptions({
      query: { include_disabled: includeDisabled },
    }),
    enabled: isAuthSetupComplete,
  });
  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: getTitleAccountUsersOptions().queryKey,
    });
  };
  const invite = useMutation({ ...addTitleAccountUserMutation() });
  const cancelInvite = useMutation({ ...cancelTitleAccountInviteMutation() });
  const disableUser = useMutation({ ...disableTitleAccountUserMutation() });
  const reenableUser = useMutation({ ...reenableTitleAccountUserMutation() });
  const makePrimary = useMutation({ ...makeTitleAccountPrimaryMutation() });
  const setAdmin = useMutation({ ...updateTitleAccountUserAdminMutation() });

  const users = (usersQuery.data?.users ?? []) as TitleUser[];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Users</h2>
        <Button type="button" onClick={() => setInviteOpen(true)}>
          Invite user
        </Button>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <Switch
          checked={includeDisabled}
          onCheckedChange={setIncludeDisabled}
          aria-label="Show disabled users"
        />
        Show disabled users
      </div>
      <ul className="divide-y rounded-md border">
        {users.map((user) => {
          const isRowPrimary =
            user.primary_id == null && user.status === "active";
          return (
            <li
              key={user.id}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
            >
              <div>
                <p className="font-medium">{user.name || user.email}</p>
                <p className="text-sm text-muted-foreground">{user.email}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">
                  {user.status === "pending"
                    ? "Pending"
                    : isRowPrimary
                      ? "Primary"
                      : "Secondary"}
                </Badge>
                {user.status === "pending" ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      void cancelInvite
                        .mutateAsync({ path: { user_id: user.id } })
                        .then(refresh);
                    }}
                  >
                    Cancel invite
                  </Button>
                ) : null}
                {user.status === "active" && !isRowPrimary ? (
                  <>
                    <div className="flex items-center gap-2 text-sm">
                      Admin
                      <Switch
                        checked={user.is_admin}
                        aria-label={`Admin for ${user.email}`}
                        onCheckedChange={(checked) => {
                          void setAdmin
                            .mutateAsync({
                              path: { user_id: user.id },
                              body: { is_admin: checked },
                            })
                            .then(refresh);
                        }}
                      />
                    </div>
                    {isPrimary ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          void makePrimary
                            .mutateAsync({ path: { user_id: user.id } })
                            .then(refresh);
                        }}
                      >
                        Make primary
                      </Button>
                    ) : null}
                    {user.id !== currentAccountId ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          void disableUser
                            .mutateAsync({ path: { user_id: user.id } })
                            .then(refresh);
                        }}
                      >
                        Disable
                      </Button>
                    ) : null}
                  </>
                ) : null}
                {user.is_disabled ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      void reenableUser
                        .mutateAsync({ path: { user_id: user.id } })
                        .then(refresh);
                    }}
                  >
                    Reenable
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite a user</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (!email.includes("@")) {
                setInviteError("Enter an email address.");
                return;
              }
              setInviteError(null);
              void invite
                .mutateAsync({ body: { email } })
                .then(() => {
                  setEmail("");
                  setInviteOpen(false);
                  refresh();
                })
                .catch((caught: unknown) => {
                  setInviteError(
                    caught instanceof Error
                      ? caught.message
                      : "Could not invite",
                  );
                });
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="title-invite-email">Email</Label>
              <Input
                id="title-invite-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            {inviteError ? (
              <p className="text-destructive text-sm">{inviteError}</p>
            ) : null}
            <DialogFooter>
              <Button type="submit">Send invite</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
